import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AppIcon, ChevronDownIcon } from '../../components/icons';
import { prefetchDashboardPath } from '../../app/routes/dashboardChunkPrefetch';
import type { NavEntry } from './navConfig';

type SidebarNavItemsProps = {
  entries: NavEntry[];
  isActive: (path: string) => boolean;
  sidebarCollapsed: boolean;
};

/** Stable product-tour anchor per nav entry, derived from the route's last segment. */
const navTourId = (path: string) => `nav-${path.split('/').filter(Boolean).pop()}`;
const groupTourId = (label: string) => `nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`;

export function SidebarNavItems({ entries, isActive, sidebarCollapsed }: SidebarNavItemsProps) {
  const location = useLocation();
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (sidebarCollapsed) return;

    const next: Record<string, boolean> = {};
    entries.forEach((entry) => {
      if (entry.type === 'group' && entry.items.some((item) => isActive(item.path))) {
        next[entry.label] = true;
      }
    });
    if (Object.keys(next).length > 0) {
      setOpenGroups((prev) => ({ ...prev, ...next }));
    }
  }, [entries, location.pathname, isActive, sidebarCollapsed]);

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const warmRoute = useCallback((path: string) => {
    prefetchDashboardPath(path);
  }, []);

  const navWarmProps = (path: string) => ({
    onMouseEnter: () => warmRoute(path),
    onFocus: () => warmRoute(path),
  });

  return (
    <>
      {entries.map((entry, index) => {
        if (entry.type === 'divider') {
          if (sidebarCollapsed) return null;
          return <div key={`divider-${index}`} className="dash-nav-divider" role="separator" />;
        }

        if (entry.type === 'link') {
          const active = isActive(entry.path);
          return (
            <Link
              key={entry.path}
              to={entry.path}
              {...navWarmProps(entry.path)}
              className={`dash-nav-item ${active ? 'dash-nav-item--active' : ''}`}
              title={sidebarCollapsed ? entry.label : undefined}
              data-tour={navTourId(entry.path)}
            >
              <AppIcon icon={entry.icon} className="dash-nav-item__icon" size={15} />
              {!sidebarCollapsed && <span className="dash-nav-item__label">{entry.label}</span>}
              {entry.badge && !sidebarCollapsed && (
                <span className="dash-nav-item__badge">{entry.badge}</span>
              )}
            </Link>
          );
        }

        const groupActive = entry.items.some((item) => isActive(item.path));
        const isOpen = openGroups[entry.label] ?? groupActive;

        if (sidebarCollapsed) {
          return (
            <React.Fragment key={entry.label}>
              {entry.items.map((child) => {
                const childActive = isActive(child.path);
                return (
                  <Link
                    key={child.path}
                    to={child.path}
                    {...navWarmProps(child.path)}
                    className={`dash-nav-item ${childActive ? 'dash-nav-item--active' : ''}`}
                    title={child.label}
                  >
                    <AppIcon icon={child.icon} className="dash-nav-item__icon" size={14} />
                  </Link>
                );
              })}
            </React.Fragment>
          );
        }

        return (
          <div key={entry.label} className={`dash-nav-group ${groupActive ? 'dash-nav-group--active' : ''}`}>
            <button
              type="button"
              className={`dash-nav-item dash-nav-group__trigger ${groupActive ? 'dash-nav-item--active' : ''}`}
              onClick={() => toggleGroup(entry.label)}
              aria-expanded={isOpen}
              data-tour={groupTourId(entry.label)}
            >
              <AppIcon icon={entry.icon} className="dash-nav-item__icon" size={15} />
              <span className="dash-nav-item__label">{entry.label}</span>
              <AppIcon
                icon={ChevronDownIcon}
                className={`dash-nav-group__chevron ${isOpen ? 'dash-nav-group__chevron--open' : ''}`}
                size={12}
              />
            </button>
            {isOpen && (
              <div className="dash-nav-group__items">
                {entry.items.map((child) => {
                  const childActive = isActive(child.path);
                  return (
                    <Link
                      key={child.path}
                      to={child.path}
                      {...navWarmProps(child.path)}
                      className={`dash-nav-item dash-nav-item--child ${childActive ? 'dash-nav-item--active' : ''}`}
                    >
                      <AppIcon icon={child.icon} className="dash-nav-item__icon" size={14} />
                      <span className="dash-nav-item__label">{child.label}</span>
                      {child.badge && <span className="dash-nav-item__badge">{child.badge}</span>}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}
